import { useEffect, useRef, useState, type RefObject } from 'react';
import { useMemoizedFn, useSize } from 'ahooks';
import { useCopilotLeftMenuCollapsed } from '@/pages/aigc/pages/Agent/hooks/useCopilotLeftMenuCollapsed';
import { TCopilotWindowMode } from '@/pages/aigc/pages/Agent/config/constant';
import { getCopilotPanelInitialWidth } from '../copilotPanelLayout';
import {
  getCopilotDefaultWindowMode,
  setCopilotDefaultWindowMode,
} from '../utils/copilotDefaultWindowMode';
import type { TDragResizeBoxRef } from '../types/window';
import { useCopilotChartResizeOnToggle } from './useCopilotChartResizeOnToggle';

/** 写入 documentElement，供 home/wrappers 内嵌槽 flex 宽使用 */
const COPILOT_PANEL_WIDTH_CSS_VAR = '--dbh-copilot-panel-width';

/** {@link useCopilotWindowLayout} 入参 */
interface IUseCopilotWindowLayoutOptions {
  /** Copilot 主会话窗是否展示（关闭时清理 CSS 变量） */
  isShowCopilot?: boolean;
}

/** {@link useCopilotWindowLayout} 返回值 */
export interface IUseCopilotWindowLayoutResult {
  /** 当前会话窗口模式（标题栏图钉控制，关闭后重置） */
  windowMode: TCopilotWindowMode;
  /** 系统默认窗口模式（更多菜单开关控制，持久化） */
  defaultWindowMode: TCopilotWindowMode;
  /** 切换当前会话窗口模式（仅影响本次打开） */
  handleChangeWindowMode: (mode: TCopilotWindowMode) => void;
  /** 切换默认窗口模式（同步当前会话窗口模式） */
  handleChangeDefaultWindowMode: (mode: TCopilotWindowMode) => void;
  isDocked: boolean;
  dragResizeBoxRef: RefObject<TDragResizeBoxRef>;
  /** 语音条等布局用宽：内嵌为初始宽，悬浮跟随 DragResizeBox 实测 */
  boxWidth: number;
}

/**
 * Copilot 窗口布局：模式持久化、内嵌时收左侧菜单、同步面板宽 CSS 变量与 boxWidth。
 * 外壳渲染由 {@link CopilotPanelShell} 负责。
 * @param options - 布局选项
 * @returns 模式、ref、宽度等供 Copilot/index 使用
 */
export function useCopilotWindowLayout({
  isShowCopilot = true,
}: IUseCopilotWindowLayoutOptions = {}): IUseCopilotWindowLayoutResult {
  const dragResizeBoxRef = useRef<TDragResizeBoxRef>(null);
  const prevShowCopilotRef = useRef(isShowCopilot);
  const [, setLeftMenuCollapsed] = useCopilotLeftMenuCollapsed();
  const [defaultWindowMode, setDefaultWindowMode] = useState<TCopilotWindowMode>(
    getCopilotDefaultWindowMode,
  );
  const [windowMode, setWindowMode] = useState<TCopilotWindowMode>(defaultWindowMode);
  const [panelInitialWidth, setPanelInitialWidth] = useState(
    getCopilotPanelInitialWidth,
  );
  const isDocked = windowMode === 'docked';

  /** Copilot 重新打开时，从 localStorage 恢复默认偏好并应用到当前会话 */
  useEffect(() => {
    if (isShowCopilot && !prevShowCopilotRef.current) {
      const latestDefaultMode = getCopilotDefaultWindowMode();
      setDefaultWindowMode(latestDefaultMode);
      setWindowMode(latestDefaultMode);
    }
    prevShowCopilotRef.current = isShowCopilot;
  }, [isShowCopilot]);

  /** 标题栏图钉：仅切换当前会话窗口模式 */
  const handleChangeWindowMode = useMemoizedFn((mode: TCopilotWindowMode) => {
    setWindowMode(mode);
  });

  /** 更多菜单默认项：写入 localStorage，并同步当前会话 */
  const handleChangeDefaultWindowMode = useMemoizedFn((mode: TCopilotWindowMode) => {
    setCopilotDefaultWindowMode(mode);
    setDefaultWindowMode(mode);
    setWindowMode(mode);
  });

  /**
   * 内嵌且 Copilot 展示时收起各业务系统左侧菜单；
   * Copilot 关闭（组件卸载）或退出内嵌时通过 cleanup 写回 false 清理全局收起态，
   * 避免其他模块菜单残留收起；当前模块菜单是否展开由 useSyncCopilotLeftMenuCollapse 保持现状。
   */
  useEffect(() => {
    if (!(isShowCopilot && isDocked)) {
      return undefined;
    }
    setLeftMenuCollapsed(true);
    return () => {
      setLeftMenuCollapsed(false);
    };
  }, [isShowCopilot, isDocked, setLeftMenuCollapsed]);

  /** 同步 --dbh-copilot-panel-width，视口 resize 时重算初始宽 */
  useEffect(() => {
    if (!isShowCopilot) {
      document.documentElement.style.removeProperty(
        COPILOT_PANEL_WIDTH_CSS_VAR,
      );
      return;
    }
    const syncPanelWidthVar = (): void => {
      const width = getCopilotPanelInitialWidth();
      setPanelInitialWidth(width);
      document.documentElement.style.setProperty(
        COPILOT_PANEL_WIDTH_CSS_VAR,
        `${width}px`,
      );
    };
    syncPanelWidthVar();
    window.addEventListener('resize', syncPanelWidthVar);
    return () => {
      window.removeEventListener('resize', syncPanelWidthVar);
      document.documentElement.style.removeProperty(
        COPILOT_PANEL_WIDTH_CSS_VAR,
      );
    };
  }, [isShowCopilot, isDocked]);

  const floatingSize = useSize(dragResizeBoxRef.current?.dropAreaRef) || {
    width: panelInitialWidth,
  };

  const boxWidth = isDocked ? panelInitialWidth : floatingSize.width;

  useCopilotChartResizeOnToggle(isShowCopilot, isDocked);

  return {
    windowMode,
    defaultWindowMode,
    handleChangeWindowMode,
    handleChangeDefaultWindowMode,
    isDocked,
    dragResizeBoxRef,
    boxWidth,
  };
}
